/**
 * FI Data Drawer
 *
 * Displays FI statement data for a consent request.
 * Shows accounts list, profile, summary, and transactions.
 * Supports multiple FI types: DEPOSIT, MUTUAL_FUNDS, EQUITIES, GST, NPS, etc.
 */

import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  Drawer,
  DrawerHeader,
  DrawerContent,
  DrawerOverlay,
  Button,
  Table,
  type TableColumn,
  Dropdown,
  DropdownTrigger,
  DropdownContent,
  DropdownItem,
  CardTabs,
  type TabItem,
} from 'equal-ds-ui';
import {
  Building2,
  Download,
  ChevronDown,
  ChevronRight,
  User,
  FileText,
  Receipt,
  Loader2,
  FileJson,
  FileType,
  FileCode,
  FileSpreadsheet,
} from 'lucide-react';
import { consentRequestAPI } from '../../../services/consent-request-api.service';
import { extractXmlPayload, wrapXmlIfNeeded } from '../../../utils/xml-utils';
import type {
  ConsentRequestRecord,
  FiDataAccountItem,
  FiDataContent,
  ConsentAnalyticsOption,
} from '../../../types/consent';

export interface FiDataDrawerProps {
  isOpen: boolean;
  onClose: () => void;
  consent: ConsentRequestRecord | null;
  analyticsOptions: ConsentAnalyticsOption[];
  onDownloadAnalytics?: (option: ConsentAnalyticsOption, format: 'JSON' | 'EXCEL', linkRefNumber: string) => Promise<void>;
}

const DEFAULT_PAGE_SIZE = 10;

// FI Type constants
type FiType =
  | 'DEPOSIT'
  | 'TERM_DEPOSIT'
  | 'RECURRING_DEPOSIT'
  | 'MUTUAL_FUNDS'
  | 'EQUITIES'
  | 'LIFE_INSURANCE'
  | 'GENERAL_INSURANCE'
  | 'GSTR1_3B'
  | 'NPS'
  | 'ETF'
  | 'AIF'
  | 'INSURANCE_POLICIES'
  | 'DEFAULT';

// Helper to get FI type from content
const getFiType = (content?: FiDataContent | null): FiType => {
  const fiType = content?.fiType?.toUpperCase();
  if (!fiType) return 'DEFAULT';

  switch (fiType) {
    case 'DEPOSIT':
    case 'SAVINGS':
    case 'CURRENT':
      return 'DEPOSIT';
    case 'TERM_DEPOSIT':
    case 'FIXED':
      return 'TERM_DEPOSIT';
    case 'RECURRING_DEPOSIT':
    case 'RECURRING':
      return 'RECURRING_DEPOSIT';
    case 'MUTUAL_FUNDS':
      return 'MUTUAL_FUNDS';
    case 'EQUITIES':
      return 'EQUITIES';
    case 'LIFE_INSURANCE':
      return 'LIFE_INSURANCE';
    case 'GENERAL_INSURANCE':
      return 'GENERAL_INSURANCE';
    case 'GSTR1_3B':
      return 'GSTR1_3B';
    case 'NPS':
      return 'NPS';
    case 'ETF':
      return 'ETF';
    case 'AIF':
      return 'AIF';
    case 'INSURANCE_POLICIES':
      return 'INSURANCE_POLICIES';
    default:
      return 'DEFAULT';
  }
};

// Helper to check if Holdings type is DEMAT
const isDematType = (content?: FiDataContent | null): boolean => {
  return content?.Summary?.Investment?.Holdings?.type === 'DEMAT';
};

export const FiDataDrawer: React.FC<FiDataDrawerProps> = ({
  isOpen,
  onClose,
  consent,
  analyticsOptions,
  onDownloadAnalytics,
}) => {
  // Loading states
  const [accountsLoading, setAccountsLoading] = useState(false);
  const [fiDataLoading, setFiDataLoading] = useState(false);
  const [downloadLoading, setDownloadLoading] = useState(false);

  // Data states
  const [accounts, setAccounts] = useState<FiDataAccountItem[]>([]);
  const [selectedAccountIndex, setSelectedAccountIndex] = useState<number>(0);
  const [error, setError] = useState<string | null>(null);

  // Collapsible sections
  const [profileExpanded, setProfileExpanded] = useState(true);
  const [summaryExpanded, setSummaryExpanded] = useState(true);
  const [transactionsExpanded, setTransactionsExpanded] = useState(true);

  // Pagination for transactions
  const [transactionPage, setTransactionPage] = useState(1);
  const [transactionPageSize, setTransactionPageSize] = useState(DEFAULT_PAGE_SIZE);

  // Analytics selection
  const [selectedAnalytics, setSelectedAnalytics] = useState<string>('');

  // Scroll shadow for account cards
  const [showRightShadow, setShowRightShadow] = useState(false);
  const accountsScrollRef = useRef<HTMLDivElement>(null);

  const customerVua = consent?.customer_vua ?? consent?.account_id ?? '';
  const consentId = consent?.consent_id ?? '';

  // Handle scroll shadow visibility for account cards
  const handleAccountsScroll = useCallback(() => {
    const container = accountsScrollRef.current;
    if (!container) return;

    const { scrollLeft, scrollWidth, clientWidth } = container;
    const isAtEnd = scrollLeft >= scrollWidth - clientWidth - 1; // -1 for rounding errors
    setShowRightShadow(!isAtEnd && scrollWidth > clientWidth);
  }, []);

  // Initialize and listen to scroll events for account cards
  useEffect(() => {
    const container = accountsScrollRef.current;
    if (!container) return;

    // Initial check
    handleAccountsScroll();

    container.addEventListener('scroll', handleAccountsScroll);
    // Also check on resize
    window.addEventListener('resize', handleAccountsScroll);

    return () => {
      container.removeEventListener('scroll', handleAccountsScroll);
      window.removeEventListener('resize', handleAccountsScroll);
    };
  }, [handleAccountsScroll, accounts.length]);

  // Fetch accounts list when drawer opens
  useEffect(() => {
    if (!isOpen || !consentId) {
      return;
    }

    const fetchAccounts = async () => {
      setAccountsLoading(true);
      setError(null);
      setAccounts([]);
      setSelectedAccountIndex(0);

      try {
        const response = await consentRequestAPI.getConsentAccountDetails({ consentId });
        const rows = response.data.artefactAccounts?.rows ?? [];
        // Include all account types, not just deposits
        setAccounts(rows.map((acc) => ({ ...acc, content: undefined })));
      } catch (err) {
        console.error('[FiDataDrawer] Failed to fetch accounts:', err);
        setError('Failed to fetch account details. Please try again later.');
      } finally {
        setAccountsLoading(false);
      }
    };

    void fetchAccounts();
  }, [isOpen, consentId]);

  // Fetch FI data when account is selected
  const fetchFiDataForAccount = useCallback(
    async (account: FiDataAccountItem, index: number) => {
      if (!consentId || !account.link_reference_number) {
        return;
      }

      // Skip if already loaded
      if (account.content) {
        return;
      }

      setFiDataLoading(true);
      setError(null);

      try {
        const response = await consentRequestAPI.getAllFIData({
          consentID: consentId,
          linkRefNumber: [account.link_reference_number],
        });

        const fiDataList = response.data?.data ?? [];
        const matchingData = fiDataList.find(
          (data) => data.maskedAccountNumber === account.masked_account_number
        );

        if (matchingData) {
          setAccounts((prev) =>
            prev.map((acc, i) => (i === index ? { ...acc, content: matchingData } : acc))
          );
        }
      } catch (err) {
        console.error('[FiDataDrawer] Failed to fetch FI data:', err);
        setError('Failed to fetch FI data. Please try again.');
      } finally {
        setFiDataLoading(false);
      }
    },
    [consentId]
  );

  // Auto-fetch FI data for selected account
  useEffect(() => {
    if (accounts.length > 0 && selectedAccountIndex < accounts.length) {
      void fetchFiDataForAccount(accounts[selectedAccountIndex], selectedAccountIndex);
    }
  }, [accounts, selectedAccountIndex, fetchFiDataForAccount]);

  const selectedAccount = accounts[selectedAccountIndex];
  const fiData = selectedAccount?.content;
  const currentFiType = getFiType(fiData);
  const isGST = currentFiType === 'GSTR1_3B';

  // Profile data extraction based on FI type
  const profileData = useMemo(() => {
    if (!fiData?.Profile) return null;

    if (isGST) {
      const businessDetails = fiData.Profile.BusinessDetails as Record<string, unknown> | undefined;
      const placeOfBusiness = fiData.Profile.PlaceofBusiness as Record<string, unknown> | undefined;
      const address = placeOfBusiness?.Address as Record<string, unknown> | undefined;

      // Get address string
      const addressFields = address ? { ...address } : {};
      delete addressFields.NatureOfBusinessList;
      const addressString = Object.values(addressFields).filter(Boolean).join(', ');

      // Get nature of business
      const natureOfBusinessList = address?.NatureOfBusinessList as Record<string, unknown> | undefined;
      const ntr = (natureOfBusinessList?.ntr as string[]) ?? [];

      return {
        type: 'GST' as const,
        fields: [
          { label: 'Business Name', value: businessDetails?.lgnm as string },
          { label: 'Trade Name', value: businessDetails?.trdnm as string },
          { label: 'Composition', value: businessDetails?.iscmp as string },
          { label: 'Taxpayer Type', value: businessDetails?.dty as string },
          { label: 'Status', value: businessDetails?.sts as string },
          { label: 'Creation Date', value: businessDetails?.apprvdt as string },
          { label: 'Address', value: addressString, fullWidth: true },
          { label: 'Nature of Business', value: ntr.join(', ') },
        ],
      };
    }

    // Standard profile (for other FI types)
    const holders = fiData.Profile?.Holders?.Holder ?? [];
    return {
      type: 'STANDARD' as const,
      holders,
    };
  }, [fiData, isGST]);

  // Summary data based on FI type
  const summaryFields = useMemo(() => {
    const summary = fiData?.Summary;
    if (!summary) return [];

    switch (currentFiType) {
      case 'DEPOSIT':
      case 'DEFAULT': {
        const summaryType = (summary.type ?? summary.accountSubType ?? '').toUpperCase();
        if (summaryType === 'SAVINGS' || summaryType === 'CURRENT' || summaryType === 'DEFAULT' || !summaryType) {
          // Get pending amount
          let pendingAmount = '';
          if (summary.Pending && Array.isArray(summary.Pending) && summary.Pending.length > 0) {
            pendingAmount = summary.Pending[0]?.amount ?? '';
          } else if (summary.PendingTxns?.PendingTxn) {
            const pendingTxns = Array.isArray(summary.PendingTxns.PendingTxn)
              ? summary.PendingTxns.PendingTxn
              : [summary.PendingTxns.PendingTxn];
            const total = pendingTxns.reduce((sum: number, txn: { amount?: string }) => {
              return sum + (parseFloat(txn.amount ?? '0') || 0);
            }, 0);
            pendingAmount = total.toString();
          }

          return [
            { label: 'Current Balance', value: summary.currentBalance, highlight: true },
            { label: 'Balance Date', value: summary.balanceDateTime },
            { label: 'Account Type', value: summary.type ?? summary.accountSubType },
            { label: 'Branch', value: summary.branch },
            { label: 'Facility', value: summary.facility },
            { label: 'Opening Date', value: summary.openingDate },
            { label: 'IFSC Code', value: summary.ifscCode ?? summary.ifsc },
            { label: 'MICR Code', value: summary.micrCode },
            { label: 'OD Limit', value: summary.currentODLimit },
            { label: 'Drawing Limit', value: summary.drawingLimit },
            { label: 'Status', value: summary.status },
            { label: 'Pending Amount', value: pendingAmount },
          ];
        }
        return [];
      }

      case 'TERM_DEPOSIT':
      case 'RECURRING_DEPOSIT': {
        const s = summary as Record<string, unknown>;
        return [
          { label: 'Current Value', value: s.currentValue as string, highlight: true },
          { label: 'Compounding Frequency', value: s.compoundingFrequency as string },
          { label: 'Account Type', value: s.accountType as string },
          { label: 'Branch', value: s.branch as string },
          { label: 'Tenure Months', value: s.tenureMonths as string },
          { label: 'Tenure Years', value: s.tenureYears as string },
          { label: 'IFSC Code', value: s.ifsc as string },
          { label: 'Interest On Maturity', value: s.interestOnMaturity as string },
          { label: 'Interest Payout', value: s.interestPayout as string },
          { label: 'Principal Amount', value: s.principalAmount as string },
          { label: 'Interest Rate', value: s.interestRate as string },
          { label: 'Maturity Amount', value: s.maturityAmount as string },
          { label: 'Maturity Date', value: s.maturityDate as string },
          { label: 'Interest Computation', value: s.interestComputation as string },
          { label: 'Tenure Days', value: s.tenureDays as string },
          { label: 'Opening Date', value: s.openingDate as string },
          { label: 'Description', value: s.description as string },
        ];
      }

      case 'MUTUAL_FUNDS': {
        const holdings = summary.Investment?.Holdings?.Holding ?? [];
        return holdings.map((h: Record<string, unknown>, idx: number) => ({
          isHolding: true,
          holdingIndex: idx,
          fields: [
            { label: 'AMC', value: h.amc as string },
            { label: 'Registrar', value: h.registrar as string },
            { label: 'ISIN', value: h.isin as string },
            { label: 'UCC', value: h.ucc as string },
            { label: 'AMFI Code', value: h.amfiCode as string },
            { label: 'Folio No', value: h.folioNo as string },
            { label: 'FATCA Status', value: h.FatcaStatus as string },
            { label: 'Closing Units', value: h.closingUnits as string },
            { label: 'Lien Units', value: h.lienUnits as string },
            { label: 'NAV', value: h.nav as string },
            { label: 'NAV Date', value: h.navDate as string },
            { label: 'Locking Units', value: h.lockinUnits as string },
          ],
        }));
      }

      case 'EQUITIES': {
        if (isDematType(fiData)) {
          const holdings = summary.Investment?.Holdings?.Holding ?? [];
          return holdings.map((h: Record<string, unknown>, idx: number) => ({
            isHolding: true,
            holdingIndex: idx,
            fields: [
              { label: 'Issuer Name', value: h.issuerName as string },
              { label: 'ISIN', value: h.isin as string },
              { label: 'Units', value: h.units as string },
              { label: 'ISIN Description', value: h.isinDescription as string },
              { label: 'Last Traded Price', value: h.lastTradedPrice as string },
            ],
          }));
        }
        return [];
      }

      case 'NPS': {
        const tier1 = summary.Holdings?.Tier1Holdings?.Tier1Holding ?? [];
        const tier2 = summary.Holdings?.Tier2Holdings?.Tier2Holding ?? [];
        const allHoldings = [
          ...tier1.map((h: Record<string, unknown>) => ({ ...h, tier: 'Tier 1' })),
          ...tier2.map((h: Record<string, unknown>) => ({ ...h, tier: 'Tier 2' })),
        ];
        return allHoldings.map((h: Record<string, unknown>, idx: number) => ({
          isHolding: true,
          holdingIndex: idx,
          fields: [
            { label: 'Tier', value: h.tier as string },
            { label: 'Scheme Name', value: h.schemeName as string },
            { label: 'Scheme ID', value: h.schemeId as string },
            { label: 'Blocked Units', value: h.blockedUnits as string },
            { label: 'Free Units', value: h.freeUnits as string },
            { label: 'NAV', value: h.nav as string },
            { label: 'Allocation %', value: h.allocationPercentage as string },
            { label: 'Total Units', value: h.totalUnits as string },
            { label: 'Total Value', value: h.totalValueOfScheme as string },
          ],
        }));
      }

      case 'ETF': {
        const holdings = summary.Investment?.Holdings?.Holding ?? [];
        return holdings.map((h: Record<string, unknown>, idx: number) => ({
          isHolding: true,
          holdingIndex: idx,
          fields: [
            { label: 'AMC', value: h.amc as string },
            { label: 'Registrar', value: h.registrar as string },
            { label: 'Scheme Code', value: h.schemeCode as string },
            { label: 'ISIN', value: h.isin as string },
            { label: 'UCC', value: h.ucc as string },
            { label: 'AMFI Code', value: h.amfiCode as string },
            { label: 'Folio No', value: h.folioNo as string },
            { label: 'Dividend Type', value: h.dividendType as string },
            { label: 'FATCA Status', value: h.FatcaStatus as string },
            { label: 'Mode', value: h.mode as string },
            { label: 'Units', value: h.units as string },
            { label: 'Closing Units', value: h.closingUnits as string },
            { label: 'Lien Units', value: h.lienUnits as string },
            { label: 'Rate', value: h.rate as string },
            { label: 'NAV', value: h.nav as string },
            { label: 'Description', value: h.isinDescription as string },
          ],
        }));
      }

      case 'AIF': {
        const holdings = summary.Investment?.Holdings?.Holding ?? [];
        return holdings.map((h: Record<string, unknown>, idx: number) => ({
          isHolding: true,
          holdingIndex: idx,
          fields: [
            { label: 'Issuer Name', value: h.issuerName as string },
            { label: 'Type of Security', value: h.typeOfSecurity as string },
            { label: 'Valuation Methodology', value: h.valuationMethodology as string },
            { label: 'Purchase Date', value: h.purchaseDate as string },
            { label: 'Purchase Price', value: h.purchasePrice as string },
            { label: 'Last Valuation Date', value: h.lastValuationDate as string },
            { label: 'MTM on Last Valuation', value: h.mtmOnLastValuation as string },
            { label: 'Holding Period', value: h.holdingPeriod as string },
            { label: 'Description', value: h.description as string },
          ],
        }));
      }

      case 'INSURANCE_POLICIES':
      case 'LIFE_INSURANCE':
      case 'GENERAL_INSURANCE': {
        const s = summary as Record<string, unknown>;
        return [
          { label: 'Policy Name', value: s.policyName as string },
          { label: 'Policy Number', value: s.policyNumber as string },
          { label: 'EIA Number', value: s.eiaNumber as string },
          { label: 'Sum Assured', value: s.sumAssured as string, highlight: true },
          { label: 'Cover Amount', value: s.coverAmount as string },
          { label: 'Tenure Months', value: s.tenureMonths as string },
          { label: 'Tenure Years', value: s.tenureYears as string },
          { label: 'Premium Amount', value: s.premiumAmount as string },
          { label: 'Policy Start Date', value: s.policyStartDate as string },
          { label: 'Policy Expiry Date', value: s.policyExpiryDate as string },
          { label: 'Policy Type', value: s.policyType as string },
          { label: 'Cover Type', value: s.coverType as string },
          { label: 'Premium Frequency', value: s.premiumFrequency as string },
          { label: 'Next Premium Due Date', value: s.nextPremiumDueDate as string },
          { label: 'Maturity Date', value: s.maturityDate as string },
          { label: 'Maturity Benefit', value: s.maturityBenefit as string },
        ];
      }

      default:
        return [
          { label: 'Current Balance', value: summary.currentBalance, highlight: true },
          { label: 'Balance Date', value: summary.balanceDateTime },
          { label: 'Account Type', value: summary.type ?? summary.accountSubType },
          { label: 'Status', value: summary.status },
        ];
    }
  }, [fiData, currentFiType]);

  // Transaction columns based on FI type
  const transactionColumns = useMemo((): TableColumn<Record<string, unknown> & { _rowId: string }>[] => {
    switch (currentFiType) {
      case 'MUTUAL_FUNDS':
        return [
          { key: 'amc', header: 'AMC', width: 150, accessor: (row) => (row.amc as string) ?? '-' },
          { key: 'txnId', header: 'Txn ID', width: 150, accessor: (row) => (row.txnId as string) ?? '-' },
          { key: 'registrar', header: 'Registrar', width: 120, accessor: (row) => (row.registrar as string) ?? '-' },
          { key: 'nav', header: 'NAV', width: 100, accessor: (row) => (row.nav as string) ?? '-' },
          { key: 'navDate', header: 'NAV Date', width: 120, accessor: (row) => (row.navDate as string) ?? '-' },
          { key: 'type', header: 'Type', width: 100, accessor: (row) => (row.type as string) ?? '-' },
          { key: 'lockInDays', header: 'Lock-in Days', width: 120, accessor: (row) => (row['lock-inDays'] as string) ?? '-' },
          { key: 'mode', header: 'Mode', width: 100, accessor: (row) => (row.mode as string) ?? '-' },
        ];

      case 'EQUITIES':
        return [
          { key: 'companyName', header: 'Company Name', width: 180, accessor: (row) => (row.companyName as string) ?? '-' },
          { key: 'equityCategory', header: 'Equity Category', width: 140, accessor: (row) => (row.equityCategory as string) ?? '-' },
          { key: 'isin', header: 'ISIN', width: 140, accessor: (row) => (row.isin as string) ?? '-' },
          { key: 'isinDescription', header: 'ISIN Description', width: 180, accessor: (row) => (row.isinDescription as string) ?? '-' },
          { key: 'transactionDateTime', header: 'Transaction Date', width: 160, accessor: (row) => (row.transactionDateTime as string) ?? '-' },
          { key: 'txnId', header: 'Txn ID', width: 150, accessor: (row) => (row.txnId as string) ?? '-' },
          { key: 'type', header: 'Type', width: 100, accessor: (row) => (row.type as string) ?? '-' },
          { key: 'units', header: 'Units', width: 100, accessor: (row) => (row.units as string) ?? '-' },
        ];

      case 'LIFE_INSURANCE':
      case 'GENERAL_INSURANCE':
        return [
          { key: 'startDate', header: 'Start Date', width: 120, accessor: (row) => (row.startDate as string) ?? '-' },
          { key: 'endDate', header: 'End Date', width: 120, accessor: (row) => (row.endDate as string) ?? '-' },
          { key: 'txnId', header: 'Txn ID', width: 150, accessor: (row) => (row.txnId as string) ?? '-' },
          { key: 'txnDate', header: 'Txn Date', width: 120, accessor: (row) => (row.txnDate as string) ?? '-' },
          { key: 'amount', header: 'Amount', width: 120, accessor: (row) => `₹${(row.amount as string) ?? '0'}` },
        ];

      default:
        // Default for DEPOSIT and other types
        return [
          { key: 'txnId', header: 'Txn ID', width: 150, accessor: (row) => (row.txnId as string) ?? '-' },
          {
            key: 'type',
            header: 'Type',
            width: 100,
            render: (_value, row) => (
              <span
                className={`px-2 py-0.5 rounded text-xs font-medium ${
                  (row.type as string)?.toUpperCase() === 'CREDIT'
                    ? 'bg-green-100 text-green-700'
                    : 'bg-red-100 text-red-700'
                }`}
              >
                {(row.type as string) ?? '-'}
              </span>
            ),
          },
          { key: 'mode', header: 'Mode', width: 120, accessor: (row) => (row.mode as string) ?? '-' },
          { key: 'amount', header: 'Amount', width: 120, accessor: (row) => `₹${(row.amount as string) ?? '0'}` },
          { key: 'currentBalance', header: 'Current Balance', width: 140, accessor: (row) => `₹${(row.currentBalance as string) ?? (row.transactionalBalance as string) ?? (row.balance as string) ?? '0'}` },
          { key: 'transactionTimestamp', header: 'Transaction Timestamp', width: 180, accessor: (row) => (row.transactionTimestamp as string) ?? (row.transactionDateTime as string) ?? '-' },
          { key: 'valueDate', header: 'Value Date', width: 120, accessor: (row) => (row.valueDate as string) ?? '-' },
          { key: 'narration', header: 'Narration', width: 200, accessor: (row) => (row.narration as string) ?? '-' },
          { key: 'reference', header: 'Reference', width: 150, accessor: (row) => (row.reference as string) ?? '-' },
        ];
    }
  }, [currentFiType]);

  // Transactions data
  const allTransactions = useMemo(() => {
    if (isGST) {
      // GST doesn't have standard transactions
      return [];
    }
    return fiData?.Transactions?.Transaction ?? [];
  }, [fiData, isGST]);

  // Add _rowId to transactions for Table component
  const transactionsWithId = useMemo(() => {
    return allTransactions.map((txn: Record<string, unknown>, idx: number) => ({
      ...txn,
      _rowId: (txn.txnId as string) ?? `txn-${idx}`,
    }));
  }, [allTransactions]);

  // Client-side pagination: slice transactions for current page
  const paginatedTransactions = useMemo(() => {
    const startIndex = (transactionPage - 1) * transactionPageSize;
    const endIndex = startIndex + transactionPageSize;
    return transactionsWithId.slice(startIndex, endIndex);
  }, [transactionsWithId, transactionPage, transactionPageSize]);

  // GST specific data
  const gstData = useMemo(() => {
    if (!isGST || !fiData?.Transactions) return null;

    const transactions = fiData.Transactions as Record<string, unknown>;
    return {
      gstr1Details: (transactions.Gstr1Details as Record<string, unknown>)?.gstr1 as unknown[] ?? [],
      gstr3bDetails: (transactions.Gstr3bDetails as Record<string, unknown>)?.gstr3b as unknown[] ?? [],
    };
  }, [fiData, isGST]);

  // Handle account selection
  const handleAccountSelect = (index: number) => {
    setSelectedAccountIndex(index);
    setTransactionPage(1);
  };

  // Get account display info
  const getAccountTypeIcon = (fiType?: string | null) => {
    const type = fiType?.toUpperCase();
    switch (type) {
      case 'DEPOSIT':
      case 'SAVINGS':
      case 'CURRENT':
        return '🏦';
      case 'TERM_DEPOSIT':
      case 'FIXED':
        return '💰';
      case 'RECURRING_DEPOSIT':
        return '📅';
      case 'MUTUAL_FUNDS':
        return '📈';
      case 'EQUITIES':
        return '📊';
      case 'LIFE_INSURANCE':
      case 'GENERAL_INSURANCE':
      case 'INSURANCE_POLICIES':
        return '🛡️';
      case 'GSTR1_3B':
        return '📋';
      case 'NPS':
        return '🏛️';
      case 'ETF':
        return '💹';
      case 'AIF':
        return '🏢';
      default:
        return '🏦';
    }
  };

  // Helper to trigger file download
  const downloadFile = (data: Blob | string, filename: string, mimeType: string) => {
    const blob = data instanceof Blob ? data : new Blob([data], { type: mimeType });
    const link = document.createElement('a');
    link.href = window.URL.createObjectURL(blob);
    link.download = filename;
    link.click();
    window.URL.revokeObjectURL(link.href);
  };

  // Handle download FI data in different formats
  const handleDownloadFiData = useCallback(async (format: 'JSON' | 'PDF' | 'XML') => {
    if (!consentId) return;

    setDownloadLoading(true);
    try {
      const linkRefNumbers = accounts.map((acc) => acc.link_reference_number).filter(Boolean) as string[];
      const timestamp = new Date().toISOString().split('T')[0];

      if (format === 'JSON') {
        const response = await consentRequestAPI.downloadFiDataJson({
          consentID: consentId,
          downloadjson: true,
        });
        const jsonString = JSON.stringify(response.data ?? response, null, 2);
        downloadFile(jsonString, `fi-data-${consentId}-${timestamp}.json`, 'application/json');
      } else if (format === 'PDF') {
        const blob = await consentRequestAPI.downloadFiDataPdf({
          consentID: consentId,
          linkRefNumber: linkRefNumbers,
        });
        downloadFile(blob, `fi-data-${consentId}-${timestamp}.pdf`, 'application/pdf');
      } else if (format === 'XML') {
        const response = await consentRequestAPI.downloadFiDataXml({
          consentID: consentId,
        });
        const xmlPayload = extractXmlPayload(response);
        const wrappedXml = wrapXmlIfNeeded(xmlPayload);
        downloadFile(wrappedXml, `fi-data-${consentId}-${timestamp}.xml`, 'application/xml');
      }
    } catch (err) {
      console.error(`[FiDataDrawer] Failed to download ${format}:`, err);
      setError(`Failed to download ${format}. Please try again.`);
    } finally {
      setDownloadLoading(false);
    }
  }, [consentId, accounts]);

  const handleDownloadAnalytics = useCallback(async (format: 'JSON' | 'EXCEL' = 'JSON') => {
    const option = analyticsOptions.find((o) => `${o.name}-${o.version}` === selectedAnalytics);
    if (option && onDownloadAnalytics && selectedAccount?.link_reference_number) {
      setDownloadLoading(true);
      try {
        await onDownloadAnalytics(option, format, selectedAccount.link_reference_number);
      } finally {
        setDownloadLoading(false);
      }
    }
  }, [analyticsOptions, selectedAnalytics, selectedAccount?.link_reference_number, onDownloadAnalytics]);

  if (!isOpen || !consent) {
    return null;
  }

  return (
    <>
      <DrawerOverlay />
      <Drawer
        variant="overlay"
        width="900px"
        className="bg-white border border-border-default shadow-2xl"
      >
        <DrawerHeader
          title="FI DATA"
          subtitle=""
          icon={<Building2 className="w-5 h-5" />}
          onClose={onClose}
          className="border-b-0"
        />

        {/* View account details + Download Raw Data - same row */}
        <div className="flex items-center justify-between px-4 py-3">
          <span className="text-sm text-text-secondary">
            View account details for: <span className="font-medium text-text-primary">{customerVua}</span>
          </span>
          {accounts.length > 0 && (
            <Dropdown>
              <DropdownTrigger asChild>
                <Button
                  variant="secondary"
                  size="sm"
                  disabled={downloadLoading}
                >
                  {downloadLoading ? (
                    <Loader2 className="w-4 h-4 animate-spin" />
                  ) : (
                    <Download className="w-4 h-4" />
                  )}
                  <span className="ml-2">Download Raw Data</span>
                  <ChevronDown className="w-4 h-4 ml-2" />
                </Button>
              </DropdownTrigger>
              <DropdownContent align="end" sideOffset={4} zIndex={1400}>
                <DropdownItem
                  onSelect={() => {
                    void handleDownloadFiData('JSON');
                  }}
                >
                  <FileJson className="w-4 h-4 text-yellow-600" />
                  <span className="ml-2">Download JSON</span>
                </DropdownItem>
                <DropdownItem
                  onSelect={() => {
                    void handleDownloadFiData('PDF');
                  }}
                >
                  <FileType className="w-4 h-4 text-red-600" />
                  <span className="ml-2">Download PDF</span>
                </DropdownItem>
                <DropdownItem
                  onSelect={() => {
                    void handleDownloadFiData('XML');
                  }}
                >
                  <FileCode className="w-4 h-4 text-blue-600" />
                  <span className="ml-2">Download XML</span>
                </DropdownItem>
              </DropdownContent>
            </Dropdown>
          )}
        </div>

        <DrawerContent className="p-4 space-y-4 overflow-y-auto">
          {/* Error State */}
          {error && (
            <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg text-sm">
              {error}
            </div>
          )}

          {/* Loading State */}
          {accountsLoading && (
            <div className="flex items-center justify-center py-12">
              <Loader2 className="w-6 h-6 animate-spin text-primary-600" />
              <span className="ml-2 text-text-secondary">Loading accounts...</span>
            </div>
          )}

          {/* No Accounts State */}
          {!accountsLoading && accounts.length === 0 && !error && (
            <div className="text-center py-12 text-text-secondary">
              <FileText className="w-12 h-12 mx-auto mb-3 text-text-tertiary" />
              <p>No accounts found for this consent.</p>
            </div>
          )}

          {/* Account Cards using Equal DS CardTabs */}
          {accounts.length > 0 && (
            <div className="space-y-3">
              <label className="text-xs text-text-secondary uppercase tracking-wider font-medium">
                Choose an account to view details:
              </label>
              <div className="relative">
                {/* Right shadow indicator when more cards to scroll */}
                {showRightShadow && (
                  <div className="pointer-events-none absolute right-0 top-0 bottom-0 z-10 w-12 bg-gradient-to-l from-white via-white/80 to-transparent" />
                )}
                <div
                  ref={accountsScrollRef}
                  className="overflow-x-auto pb-2 scrollbar-hide"
                >
                  <CardTabs
                    tabs={accounts.map((account, index) => ({
                      id: account.link_reference_number ?? `account-${index}`,
                      label: account.fip_id ?? 'Bank',
                      metadata: account.masked_account_number ?? '',
                    }))}
                    activeTab={accounts[selectedAccountIndex]?.link_reference_number ?? `account-${selectedAccountIndex}`}
                    onTabChange={(tabId) => {
                      const index = accounts.findIndex(
                        (acc, idx) => (acc.link_reference_number ?? `account-${idx}`) === tabId
                      );
                      if (index >= 0) handleAccountSelect(index);
                    }}
                    renderTab={(tab: TabItem, isActive: boolean, onClick: () => void) => {
                      const index = accounts.findIndex(
                        (acc, idx) => (acc.link_reference_number ?? `account-${idx}`) === tab.id
                      );
                      const account = accounts[index];
                      return (
                        <button
                          key={tab.id}
                          role="tab"
                          aria-selected={isActive}
                          tabIndex={isActive ? 0 : -1}
                          onClick={onClick}
                          className={`flex items-center gap-3 p-3 rounded-lg border transition-all duration-200 min-w-[200px] ${
                            isActive
                              ? 'bg-background-secondary border-primary-400 border-b-2 border-b-primary-400 shadow-sm'
                              : 'bg-transparent border-primary-300 hover:bg-background-primary hover:border-primary-400'
                          }`}
                        >
                          <span className="text-2xl">{getAccountTypeIcon(account?.fi_type)}</span>
                          <div className="text-left">
                            <div className="text-sm font-medium text-text-primary">
                              {tab.label}
                            </div>
                            <div className="text-xs text-text-tertiary">
                              {tab.metadata}
                            </div>
                          </div>
                        </button>
                      );
                    }}
                  />
                </div>
              </div>
            </div>
          )}

          {/* Download Analytics Card - Above the data sections */}
          {selectedAccount && analyticsOptions.length > 0 && currentFiType === 'DEPOSIT' && (
            <div className="p-4 bg-white border border-border-default rounded-lg shadow-sm">
              <div className="flex items-center justify-between gap-3">
                <span className="text-sm font-medium text-text-primary">Request Analytics</span>
                <div className="flex items-center gap-2">
                  <Dropdown>
                    <DropdownTrigger asChild>
                      <Button
                        variant="secondary"
                        size="sm"
                      >
                        {selectedAnalytics ? selectedAnalytics : 'Select Analytics Type'}
                        <ChevronDown className="w-4 h-4 ml-2" />
                      </Button>
                    </DropdownTrigger>
                    <DropdownContent align="end" sideOffset={4} zIndex={1400}>
                      {analyticsOptions.map((opt) => (
                        <DropdownItem
                          key={`${opt.name}-${opt.version}`}
                          onSelect={() => setSelectedAnalytics(`${opt.name}-${opt.version}`)}
                        >
                          {opt.name} - v{opt.version}
                        </DropdownItem>
                      ))}
                    </DropdownContent>
                  </Dropdown>
                  <Dropdown>
                    <DropdownTrigger asChild>
                      <Button
                        variant="primary"
                        size="sm"
                        disabled={!selectedAnalytics || downloadLoading}
                      >
                        {downloadLoading ? (
                          <Loader2 className="w-4 h-4 animate-spin" />
                        ) : (
                          <Download className="w-4 h-4" />
                        )}
                        <span className="ml-2">Download</span>
                        <ChevronDown className="w-4 h-4 ml-2" />
                      </Button>
                    </DropdownTrigger>
                    <DropdownContent align="end" sideOffset={4} zIndex={1400}>
                      <DropdownItem
                        onSelect={() => {
                          void handleDownloadAnalytics('JSON');
                        }}
                      >
                        <FileJson className="w-4 h-4 text-yellow-600" />
                        <span className="ml-2">Download JSON</span>
                      </DropdownItem>
                      <DropdownItem
                        onSelect={() => {
                          void handleDownloadAnalytics('EXCEL');
                        }}
                      >
                        <FileSpreadsheet className="w-4 h-4 text-green-600" />
                        <span className="ml-2">Download Excel</span>
                      </DropdownItem>
                    </DropdownContent>
                  </Dropdown>
                </div>
              </div>
            </div>
          )}

          {/* FI Data Loading */}
          {fiDataLoading && (
            <div className="flex items-center justify-center py-8">
              <Loader2 className="w-5 h-5 animate-spin text-primary-600" />
              <span className="ml-2 text-text-secondary">Loading account data...</span>
            </div>
          )}

          {/* Profile Section */}
          {fiData && (
            <div className="border border-border-default rounded-lg overflow-hidden">
              <button
                type="button"
                onClick={() => setProfileExpanded(!profileExpanded)}
                className="w-full flex items-center gap-3 px-4 py-3 bg-slate-50 hover:bg-slate-100 transition-colors"
              >
                {profileExpanded ? (
                  <ChevronDown className="w-4 h-4 text-text-tertiary" />
                ) : (
                  <ChevronRight className="w-4 h-4 text-text-tertiary" />
                )}
                <div className="w-8 h-8 rounded-lg bg-primary-100 flex items-center justify-center">
                  <User className="w-4 h-4 text-primary-600" />
                </div>
                <span className="text-sm font-semibold text-text-primary uppercase tracking-wider">
                  Profile
                </span>
              </button>
              {profileExpanded && profileData && (
                <div className="p-4 grid grid-cols-2 md:grid-cols-3 gap-4">
                  {profileData.type === 'GST' ? (
                    profileData.fields.map((field, idx) => (
                      <div key={idx} className={field.fullWidth ? 'col-span-2 md:col-span-3' : ''}>
                        <ProfileField label={field.label} value={field.value} />
                      </div>
                    ))
                  ) : profileData.holders.length > 0 ? (
                    profileData.holders.map((holder, idx) => (
                      <React.Fragment key={idx}>
                        <ProfileField label="Name" value={holder.name} />
                        <ProfileField label="DOB" value={holder.dob} />
                        <ProfileField label="Mobile" value={holder.mobile} />
                        <ProfileField label="Email" value={holder.email} />
                        <ProfileField label="PAN" value={holder.pan} />
                        <ProfileField
                          label="CKYC Compliance"
                          value={holder.ckycCompliance ?? holder.ckycRegistered}
                        />
                        <div className="col-span-2 md:col-span-3">
                          <ProfileField label="Address" value={holder.address} />
                        </div>
                      </React.Fragment>
                    ))
                  ) : (
                    <p className="text-sm text-text-tertiary col-span-3">
                      No profile information available.
                    </p>
                  )}
                </div>
              )}
            </div>
          )}

          {/* Summary Section */}
          {fiData && !isGST && (
            <div className="border border-border-default rounded-lg overflow-hidden">
              <button
                type="button"
                onClick={() => setSummaryExpanded(!summaryExpanded)}
                className="w-full flex items-center gap-3 px-4 py-3 bg-slate-50 hover:bg-slate-100 transition-colors"
              >
                {summaryExpanded ? (
                  <ChevronDown className="w-4 h-4 text-text-tertiary" />
                ) : (
                  <ChevronRight className="w-4 h-4 text-text-tertiary" />
                )}
                <div className="w-8 h-8 rounded-lg bg-primary-100 flex items-center justify-center">
                  <FileText className="w-4 h-4 text-primary-600" />
                </div>
                <span className="text-sm font-semibold text-text-primary uppercase tracking-wider">
                  Summary
                </span>
              </button>
              {summaryExpanded && (
                <div className="p-4">
                  {summaryFields.length > 0 ? (
                    // Check if we have holdings (array of objects with fields)
                    summaryFields[0] && 'isHolding' in summaryFields[0] ? (
                      <div className="space-y-6">
                        {(summaryFields as { isHolding: boolean; holdingIndex: number; fields: { label: string; value: string }[] }[]).map((holding, hIdx) => (
                          <div key={hIdx} className="border-b border-border-default pb-4 last:border-0 last:pb-0">
                            <div className="text-xs text-text-tertiary uppercase tracking-wider mb-3 font-medium">
                              Holding {holding.holdingIndex + 1}
                            </div>
                            <div className="grid grid-cols-3 gap-4">
                              {holding.fields.map((field, fIdx) => (
                                <SummaryField key={fIdx} label={field.label} value={field.value} />
                              ))}
                            </div>
                          </div>
                        ))}
                      </div>
                    ) : (
                      <div className="grid grid-cols-3 gap-4">
                        {(summaryFields as { label: string; value?: string; highlight?: boolean }[]).map((field, idx) => (
                          <SummaryField key={idx} label={field.label} value={field.value} highlight={field.highlight} />
                        ))}
                      </div>
                    )
                  ) : (
                    <p className="text-sm text-text-tertiary">No summary information available.</p>
                  )}
                </div>
              )}
            </div>
          )}

          {/* Transactions Section (Non-GST) */}
          {fiData && !isGST && (
            <div className="border border-border-default rounded-lg">
              <button
                type="button"
                onClick={() => setTransactionsExpanded(!transactionsExpanded)}
                className="w-full flex items-center gap-3 px-4 py-3 bg-slate-50 hover:bg-slate-100 transition-colors rounded-t-lg"
              >
                {transactionsExpanded ? (
                  <ChevronDown className="w-4 h-4 text-text-tertiary" />
                ) : (
                  <ChevronRight className="w-4 h-4 text-text-tertiary" />
                )}
                <div className="w-8 h-8 rounded-lg bg-primary-100 flex items-center justify-center">
                  <Receipt className="w-4 h-4 text-primary-600" />
                </div>
                <span className="text-sm font-semibold text-text-primary uppercase tracking-wider">
                  Transactions
                </span>
                <span className="ml-2 text-xs text-text-tertiary">
                  ({allTransactions.length} records)
                </span>
              </button>
              {transactionsExpanded && (
                <div className="p-4 overflow-visible">
                  {allTransactions.length > 0 ? (
                    <Table
                      columns={transactionColumns}
                      data={paginatedTransactions}
                      pagination={{
                        currentPage: transactionPage,
                        pageSize: transactionPageSize,
                        totalItems: allTransactions.length,
                        onPageChange: setTransactionPage,
                        onPageSizeChange: (size: number) => {
                          setTransactionPageSize(size);
                          setTransactionPage(1);
                        },
                        pageSizeOptions: [10, 25, 50, 100],
                        showPageSizeSelector: true,
                        dropdownZIndex: 1500,
                      }}
                    />
                  ) : (
                    <p className="text-sm text-text-tertiary">No transactions available.</p>
                  )}
                </div>
              )}
            </div>
          )}

          {/* GST Transactions Section */}
          {fiData && isGST && gstData && (
            <div className="border border-border-default rounded-lg overflow-hidden">
              <button
                type="button"
                onClick={() => setTransactionsExpanded(!transactionsExpanded)}
                className="w-full flex items-center gap-3 px-4 py-3 bg-slate-50 hover:bg-slate-100 transition-colors"
              >
                {transactionsExpanded ? (
                  <ChevronDown className="w-4 h-4 text-text-tertiary" />
                ) : (
                  <ChevronRight className="w-4 h-4 text-text-tertiary" />
                )}
                <div className="w-8 h-8 rounded-lg bg-primary-100 flex items-center justify-center">
                  <Receipt className="w-4 h-4 text-primary-600" />
                </div>
                <span className="text-sm font-semibold text-text-primary uppercase tracking-wider">
                  Transactions
                </span>
              </button>
              {transactionsExpanded && (
                <div className="p-4 space-y-6">
                  {/* GSTR1 Details */}
                  {gstData.gstr1Details.map((gstr1: unknown, idx: number) => {
                    const g = gstr1 as Record<string, unknown>;
                    const b2bDetails = (g.b2b_dtls as Record<string, unknown>)?.b2b as unknown[] ?? [];
                    return (
                      <div key={idx} className="space-y-4">
                        <h5 className="text-sm font-semibold text-text-primary">GSTR1 Details</h5>
                        <div className="grid grid-cols-4 gap-4">
                          <ProfileField label="Date of Filing" value={g.dof as string} />
                          <ProfileField label="Status" value={g.status as string} />
                          <ProfileField label="Tax Period" value={g.ret_prd as string} />
                          <ProfileField label="Filing Frequency" value={g.filingFrequency as string} />
                        </div>
                        {b2bDetails.length > 0 && (
                          <div>
                            <h6 className="text-xs font-medium text-text-secondary mb-2">B2B Details</h6>
                            <div className="overflow-x-auto">
                              <table className="min-w-full text-sm">
                                <thead className="bg-slate-50">
                                  <tr>
                                    <th className="px-3 py-2 text-left text-xs font-medium text-text-secondary">Invoice No</th>
                                    <th className="px-3 py-2 text-left text-xs font-medium text-text-secondary">Invoice Date</th>
                                    <th className="px-3 py-2 text-left text-xs font-medium text-text-secondary">Place of Supply</th>
                                    <th className="px-3 py-2 text-left text-xs font-medium text-text-secondary">Invoice Value</th>
                                    <th className="px-3 py-2 text-left text-xs font-medium text-text-secondary">Counter Party GSTIN</th>
                                    <th className="px-3 py-2 text-left text-xs font-medium text-text-secondary">Reverse Charge</th>
                                    <th className="px-3 py-2 text-left text-xs font-medium text-text-secondary">Invoice Type</th>
                                  </tr>
                                </thead>
                                <tbody className="divide-y divide-border-default">
                                  {b2bDetails.map((b2b: unknown, bIdx: number) => {
                                    const b = b2b as Record<string, unknown>;
                                    return (
                                      <tr key={bIdx}>
                                        <td className="px-3 py-2">{(b.inum as string) ?? '-'}</td>
                                        <td className="px-3 py-2">{(b.idt as string) ?? '-'}</td>
                                        <td className="px-3 py-2">{(b.pos as string) ?? '-'}</td>
                                        <td className="px-3 py-2">{(b.val as string) ?? '-'}</td>
                                        <td className="px-3 py-2">{(b.ctin as string) ?? '-'}</td>
                                        <td className="px-3 py-2">{(b.rchrg as string) ?? '-'}</td>
                                        <td className="px-3 py-2">{(b.inv_typ as string) ?? '-'}</td>
                                      </tr>
                                    );
                                  })}
                                </tbody>
                              </table>
                            </div>
                          </div>
                        )}
                      </div>
                    );
                  })}

                  {/* GSTR3B Details */}
                  {gstData.gstr3bDetails.map((gstr3b: unknown, idx: number) => {
                    const g = gstr3b as Record<string, unknown>;
                    const txPmt = g.tx_pmt as Record<string, unknown> | undefined;
                    const txPyList = (txPmt?.tx_py_list as Record<string, unknown>)?.tx_py as unknown[] | undefined;
                    const itcElg = g.itc_elg as Record<string, unknown> | undefined;
                    const itcNet = itcElg?.itc_net as Record<string, unknown> | undefined;
                    const itcAvlList = (itcElg?.itc_avl_list as Record<string, unknown>)?.itc_avl as unknown[] | undefined;
                    const itcRevList = (itcElg?.itc_rev_list as Record<string, unknown>)?.itc_rev as unknown[] | undefined;
                    const itcInelgList = (itcElg?.itc_inelg_list as Record<string, unknown>)?.itc_inelg as unknown[] | undefined;
                    const intrLtfee = g.intr_ltfee as Record<string, unknown> | undefined;
                    const intrDetails = intrLtfee?.intr_details as Record<string, unknown> | undefined;
                    const ltfeeDetails = intrLtfee?.ltfee_details as Record<string, unknown> | undefined;
                    const pdcashDtls = g.pdcash_dtls as Record<string, unknown> | undefined;
                    const pditc = pdcashDtls?.pditc as Record<string, unknown> | undefined;
                    const pdcash = (pdcashDtls?.pdcash_list as Record<string, unknown>)?.pdcash as Record<string, unknown> | undefined;
                    const supDetails = g.sup_details as Record<string, unknown> | undefined;
                    const isupRev = supDetails?.isup_rev as Record<string, unknown> | undefined;
                    const osupDet = supDetails?.osup_det as Record<string, unknown> | undefined;
                    const osupZero = supDetails?.osup_zero as Record<string, unknown> | undefined;
                    const osupNongst = supDetails?.osup_nongst as Record<string, unknown> | undefined;
                    const osupNilExmp = supDetails?.osup_nil_exmp as Record<string, unknown> | undefined;

                    return (
                      <div key={idx} className="space-y-6">
                        <h5 className="text-sm font-semibold text-text-primary">GSTR3B Details</h5>
                        <div className="grid grid-cols-4 gap-4">
                          <ProfileField label="Date of Filing" value={g.dof as string} />
                          <ProfileField label="Status" value={g.status as string} />
                          <ProfileField label="Tax Period" value={g.ret_prd as string} />
                          <ProfileField label="Filing Frequency" value={g.filingFrequency as string} />
                        </div>

                        {/* List of Payable Tax */}
                        {txPyList && txPyList.length > 0 && (
                          <div className="space-y-4">
                            <h6 className="text-xs font-medium text-text-secondary">List of Payable Tax</h6>
                            {txPyList.map((taxInfo: unknown, tIdx: number) => {
                              const t = taxInfo as Record<string, unknown>;
                              const cess = t.cess as Record<string, unknown> | undefined;
                              const cgst = t.cgst as Record<string, unknown> | undefined;
                              const igst = t.igst as Record<string, unknown> | undefined;
                              const sgst = t.sgst as Record<string, unknown> | undefined;
                              return (
                                <div key={tIdx} className="space-y-3 border-l-2 border-primary-200 pl-4">
                                  {/* CESS Details */}
                                  <div>
                                    <h6 className="text-xs font-medium text-text-secondary mb-2">CESS Details</h6>
                                    <div className="grid grid-cols-3 gap-4">
                                      <ProfileField label="Tax Payable" value={(cess?.tx as string) ?? '0'} />
                                      <ProfileField label="Interest Payable" value={(cess?.intr as string) ?? '0'} />
                                      <ProfileField label="Fees" value={(cess?.fee as string) ?? '0'} />
                                    </div>
                                  </div>
                                  {/* CGST Details */}
                                  <div>
                                    <h6 className="text-xs font-medium text-text-secondary mb-2">CGST Details</h6>
                                    <div className="grid grid-cols-3 gap-4">
                                      <ProfileField label="Tax Payable" value={(cgst?.tx as string) ?? '0'} />
                                      <ProfileField label="Interest Payable" value={(cgst?.intr as string) ?? '0'} />
                                      <ProfileField label="Fees" value={(cgst?.fee as string) ?? '0'} />
                                    </div>
                                  </div>
                                  {/* IGST Details */}
                                  <div>
                                    <h6 className="text-xs font-medium text-text-secondary mb-2">IGST Details</h6>
                                    <div className="grid grid-cols-3 gap-4">
                                      <ProfileField label="Tax Payable" value={(igst?.tx as string) ?? '0'} />
                                      <ProfileField label="Interest Payable" value={(igst?.intr as string) ?? '0'} />
                                      <ProfileField label="Fees" value={(igst?.fee as string) ?? '0'} />
                                    </div>
                                  </div>
                                  {/* SGST Details */}
                                  <div>
                                    <h6 className="text-xs font-medium text-text-secondary mb-2">SGST Details</h6>
                                    <div className="grid grid-cols-3 gap-4">
                                      <ProfileField label="Tax Payable" value={(sgst?.tx as string) ?? '0'} />
                                      <ProfileField label="Interest Payable" value={(sgst?.intr as string) ?? '0'} />
                                      <ProfileField label="Fees" value={(sgst?.fee as string) ?? '0'} />
                                    </div>
                                  </div>
                                  {/* Transaction Description */}
                                  {(t.trans_desc as string | undefined) && (
                                    <div>
                                      <h6 className="text-xs font-medium text-text-secondary mb-2">Transaction Description</h6>
                                      <p className="text-sm text-text-primary">{t.trans_desc as string}</p>
                                    </div>
                                  )}
                                </div>
                              );
                            })}
                          </div>
                        )}

                        {/* Eligible ITC Section */}
                        <div className="space-y-4">
                          <h6 className="text-xs font-medium text-text-secondary">Eligible ITC</h6>

                          {/* Net ITC */}
                          {itcNet && (
                            <div>
                              <h6 className="text-xs font-medium text-text-tertiary mb-2">Net ITC</h6>
                              <div className="grid grid-cols-4 gap-4">
                                <ProfileField label="Central Tax" value={(itcNet.camt as string) ?? '0'} />
                                <ProfileField label="Integrated Tax" value={(itcNet.iamt as string) ?? '0'} />
                                <ProfileField label="State Tax" value={(itcNet.samt as string) ?? '0'} />
                                <ProfileField label="CESS" value={(itcNet.csamt as string) ?? '0'} />
                              </div>
                            </div>
                          )}

                          {/* Available ITC Table */}
                          {itcAvlList && itcAvlList.length > 0 && (
                            <div>
                              <h6 className="text-xs font-medium text-text-tertiary mb-2">Available ITC</h6>
                              <div className="overflow-x-auto">
                                <table className="min-w-full text-sm">
                                  <thead className="bg-slate-50">
                                    <tr>
                                      <th className="px-3 py-2 text-left text-xs font-medium text-text-secondary">Central Tax</th>
                                      <th className="px-3 py-2 text-left text-xs font-medium text-text-secondary">Integrated Tax</th>
                                      <th className="px-3 py-2 text-left text-xs font-medium text-text-secondary">State Tax</th>
                                      <th className="px-3 py-2 text-left text-xs font-medium text-text-secondary">Type</th>
                                      <th className="px-3 py-2 text-left text-xs font-medium text-text-secondary">CESS</th>
                                    </tr>
                                  </thead>
                                  <tbody className="divide-y divide-border-default">
                                    {itcAvlList.map((itc: unknown, iIdx: number) => {
                                      const i = itc as Record<string, unknown>;
                                      return (
                                        <tr key={iIdx}>
                                          <td className="px-3 py-2">{(i.camt as string) ?? '-'}</td>
                                          <td className="px-3 py-2">{(i.iamt as string) ?? '-'}</td>
                                          <td className="px-3 py-2">{(i.samt as string) ?? '-'}</td>
                                          <td className="px-3 py-2">{(i.type as string) ?? '-'}</td>
                                          <td className="px-3 py-2">{(i.csamt as string) ?? '-'}</td>
                                        </tr>
                                      );
                                    })}
                                  </tbody>
                                </table>
                              </div>
                            </div>
                          )}

                          {/* Reversed ITC Table */}
                          {itcRevList && itcRevList.length > 0 && (
                            <div>
                              <h6 className="text-xs font-medium text-text-tertiary mb-2">Reversed ITC</h6>
                              <div className="overflow-x-auto">
                                <table className="min-w-full text-sm">
                                  <thead className="bg-slate-50">
                                    <tr>
                                      <th className="px-3 py-2 text-left text-xs font-medium text-text-secondary">Central Tax</th>
                                      <th className="px-3 py-2 text-left text-xs font-medium text-text-secondary">Integrated Tax</th>
                                      <th className="px-3 py-2 text-left text-xs font-medium text-text-secondary">State Tax</th>
                                      <th className="px-3 py-2 text-left text-xs font-medium text-text-secondary">Type</th>
                                      <th className="px-3 py-2 text-left text-xs font-medium text-text-secondary">CESS</th>
                                    </tr>
                                  </thead>
                                  <tbody className="divide-y divide-border-default">
                                    {itcRevList.map((itc: unknown, iIdx: number) => {
                                      const i = itc as Record<string, unknown>;
                                      return (
                                        <tr key={iIdx}>
                                          <td className="px-3 py-2">{(i.camt as string) ?? '-'}</td>
                                          <td className="px-3 py-2">{(i.iamt as string) ?? '-'}</td>
                                          <td className="px-3 py-2">{(i.samt as string) ?? '-'}</td>
                                          <td className="px-3 py-2">{(i.type as string) ?? '-'}</td>
                                          <td className="px-3 py-2">{(i.csamt as string) ?? '-'}</td>
                                        </tr>
                                      );
                                    })}
                                  </tbody>
                                </table>
                              </div>
                            </div>
                          )}

                          {/* Ineligible ITC Table */}
                          {itcInelgList && itcInelgList.length > 0 && (
                            <div>
                              <h6 className="text-xs font-medium text-text-tertiary mb-2">Ineligible ITC</h6>
                              <div className="overflow-x-auto">
                                <table className="min-w-full text-sm">
                                  <thead className="bg-slate-50">
                                    <tr>
                                      <th className="px-3 py-2 text-left text-xs font-medium text-text-secondary">Central Tax</th>
                                      <th className="px-3 py-2 text-left text-xs font-medium text-text-secondary">Integrated Tax</th>
                                      <th className="px-3 py-2 text-left text-xs font-medium text-text-secondary">State Tax</th>
                                      <th className="px-3 py-2 text-left text-xs font-medium text-text-secondary">Type</th>
                                      <th className="px-3 py-2 text-left text-xs font-medium text-text-secondary">CESS</th>
                                    </tr>
                                  </thead>
                                  <tbody className="divide-y divide-border-default">
                                    {itcInelgList.map((itc: unknown, iIdx: number) => {
                                      const i = itc as Record<string, unknown>;
                                      return (
                                        <tr key={iIdx}>
                                          <td className="px-3 py-2">{(i.camt as string) ?? '-'}</td>
                                          <td className="px-3 py-2">{(i.iamt as string) ?? '-'}</td>
                                          <td className="px-3 py-2">{(i.samt as string) ?? '-'}</td>
                                          <td className="px-3 py-2">{(i.type as string) ?? '-'}</td>
                                          <td className="px-3 py-2">{(i.csamt as string) ?? '-'}</td>
                                        </tr>
                                      );
                                    })}
                                  </tbody>
                                </table>
                              </div>
                            </div>
                          )}
                        </div>

                        {/* Interest and Late Fee */}
                        {intrLtfee && (
                          <div className="space-y-4">
                            <h6 className="text-xs font-medium text-text-secondary">Interest and Late Fee</h6>

                            {/* Interest Details */}
                            {intrDetails && (
                              <div>
                                <h6 className="text-xs font-medium text-text-tertiary mb-2">Interest Details</h6>
                                <div className="grid grid-cols-4 gap-4">
                                  <ProfileField label="Central Tax" value={(intrDetails.camt as string) ?? '0'} />
                                  <ProfileField label="Integrated Tax" value={(intrDetails.iamt as string) ?? '0'} />
                                  <ProfileField label="State Tax" value={(intrDetails.samt as string) ?? '0'} />
                                  <ProfileField label="CESS" value={(intrDetails.csamt as string) ?? '0'} />
                                </div>
                              </div>
                            )}

                            {/* Late Fees */}
                            {ltfeeDetails && (
                              <div>
                                <h6 className="text-xs font-medium text-text-tertiary mb-2">Late Fees</h6>
                                <div className="grid grid-cols-4 gap-4">
                                  <ProfileField label="Central Tax" value={(ltfeeDetails.camt as string) ?? '0'} />
                                  <ProfileField label="State Tax" value={(ltfeeDetails.samt as string) ?? '0'} />
                                </div>
                              </div>
                            )}
                          </div>
                        )}

                        {/* Cash Paid Details */}
                        {pdcashDtls && (
                          <div className="space-y-4">
                            <h6 className="text-xs font-medium text-text-secondary">Cash Paid Details</h6>

                            {/* Credit Paid */}
                            {pditc && (
                              <div>
                                <h6 className="text-xs font-medium text-text-tertiary mb-2">Credit Paid</h6>
                                <div className="grid grid-cols-3 gap-4">
                                  <ProfileField label="CGST paid using CGST" value={(pditc.c_pdc as string) ?? '0'} />
                                  <ProfileField label="CGST paid using IGST" value={(pditc.c_pdi as string) ?? '0'} />
                                  <ProfileField label="IGST paid using CGST" value={(pditc.i_pdc as string) ?? '0'} />
                                  <ProfileField label="IGST paid using IGST" value={(pditc.i_pdi as string) ?? '0'} />
                                  <ProfileField label="IGST paid using SGST" value={(pditc.i_pds as string) ?? '0'} />
                                  <ProfileField label="SGST paid using IGST" value={(pditc.s_pdi as string) ?? '0'} />
                                  <ProfileField label="SGST paid using SGST" value={(pditc.s_pds as string) ?? '0'} />
                                  <ProfileField label="CESS paid using CESS" value={(pditc.cs_pdcs as string) ?? '0'} />
                                  <ProfileField label="Transaction Description" value={(pditc.trans_desc as string) ?? '-'} />
                                </div>
                              </div>
                            )}

                            {/* Cash Paid */}
                            {pdcash && (
                              <div>
                                <h6 className="text-xs font-medium text-text-tertiary mb-2">Cash Paid</h6>
                                <div className="grid grid-cols-3 gap-4">
                                  <ProfileField label="CGST paid" value={(pdcash.cpd as string) ?? '0'} />
                                  <ProfileField label="IGST paid" value={(pdcash.ipd as string) ?? '0'} />
                                  <ProfileField label="SGST paid" value={(pdcash.spd as string) ?? '0'} />
                                  <ProfileField label="CESS Paid" value={(pdcash.cspd as string) ?? '0'} />
                                  <ProfileField label="Interest paid against CGST" value={(pdcash.c_intrpd as string) ?? '0'} />
                                  <ProfileField label="Late fee against CGST" value={(pdcash.c_lfeepd as string) ?? '0'} />
                                  <ProfileField label="Interest paid against IGST" value={(pdcash.i_intrpd as string) ?? '0'} />
                                  <ProfileField label="Interest paid against SGST" value={(pdcash.s_intrpd as string) ?? '0'} />
                                  <ProfileField label="Late fee against SGST" value={(pdcash.s_lfeepd as string) ?? '0'} />
                                  <ProfileField label="Interest paid against CESS" value={(pdcash.cs_intrpd as string) ?? '0'} />
                                  <ProfileField label="Transaction Description" value={(pdcash.trans_desc as string) ?? '-'} />
                                </div>
                              </div>
                            )}
                          </div>
                        )}

                        {/* Outward Supplies and Inward Supplies */}
                        {supDetails && (
                          <div className="space-y-4">
                            <h6 className="text-xs font-medium text-text-secondary">Outward Supplies and Inward Supplies liable to reverse charge</h6>

                            {/* Inward Supplies (Reverse Charge) */}
                            {isupRev && (
                              <div>
                                <h6 className="text-xs font-medium text-text-tertiary mb-2">Inward Supplies</h6>
                                <div className="grid grid-cols-5 gap-4">
                                  <ProfileField label="Central Tax" value={(isupRev.camt as string) ?? '0'} />
                                  <ProfileField label="Integrated Tax" value={(isupRev.iamt as string) ?? '0'} />
                                  <ProfileField label="State Tax" value={(isupRev.samt as string) ?? '0'} />
                                  <ProfileField label="CESS" value={(isupRev.csamt as string) ?? '0'} />
                                  <ProfileField label="Total Taxable Value" value={(isupRev.txval as string) ?? '0'} />
                                </div>
                              </div>
                            )}

                            {/* Outward taxable Supplies */}
                            {osupDet && (
                              <div>
                                <h6 className="text-xs font-medium text-text-tertiary mb-2">Outward taxable Supplies</h6>
                                <div className="grid grid-cols-5 gap-4">
                                  <ProfileField label="Central Tax" value={(osupDet.camt as string) ?? '0'} />
                                  <ProfileField label="Integrated Tax" value={(osupDet.iamt as string) ?? '0'} />
                                  <ProfileField label="State Tax" value={(osupDet.samt as string) ?? '0'} />
                                  <ProfileField label="CESS" value={(osupDet.csamt as string) ?? '0'} />
                                  <ProfileField label="Total Taxable Value" value={(osupDet.txval as string) ?? '0'} />
                                </div>
                              </div>
                            )}

                            {/* Outward taxable Supplies (Zero Rated) */}
                            {osupZero && (
                              <div>
                                <h6 className="text-xs font-medium text-text-tertiary mb-2">Outward taxable Supplies (Zero Rated)</h6>
                                <div className="grid grid-cols-5 gap-4">
                                  <ProfileField label="Integrated Tax" value={(osupZero.iamt as string) ?? '0'} />
                                  <ProfileField label="CESS" value={(osupZero.csamt as string) ?? '0'} />
                                  <ProfileField label="Total Taxable Value" value={(osupZero.txval as string) ?? '0'} />
                                </div>
                              </div>
                            )}

                            {/* Non-GST Outward Supplies */}
                            {osupNongst && (
                              <div>
                                <h6 className="text-xs font-medium text-text-tertiary mb-2">Non-GST Outward Supplies</h6>
                                <div className="grid grid-cols-5 gap-4">
                                  <ProfileField label="Total Taxable Value" value={(osupNongst.txval as string) ?? '0'} />
                                </div>
                              </div>
                            )}

                            {/* Other Outward Supplies (Exempted) */}
                            {osupNilExmp && (
                              <div>
                                <h6 className="text-xs font-medium text-text-tertiary mb-2">Other Outward Supplies (Exempted)</h6>
                                <div className="grid grid-cols-5 gap-4">
                                  <ProfileField label="Total Taxable Value" value={(osupNilExmp.txval as string) ?? '0'} />
                                </div>
                              </div>
                            )}
                          </div>
                        )}
                      </div>
                    );
                  })}

                  {gstData.gstr1Details.length === 0 && gstData.gstr3bDetails.length === 0 && (
                    <p className="text-sm text-text-tertiary">No GST transaction data available.</p>
                  )}
                </div>
              )}
            </div>
          )}
        </DrawerContent>
      </Drawer>
    </>
  );
};

// Helper Components
const ProfileField: React.FC<{
  label: string;
  value?: string | null;
}> = ({ label, value }) => (
  <div>
    <div className="text-xs text-text-tertiary uppercase tracking-wider">{label}</div>
    <div className="text-sm text-text-primary font-medium mt-0.5">{value ?? '-'}</div>
  </div>
);

const SummaryField: React.FC<{
  label: string;
  value?: string | null;
  highlight?: boolean;
}> = ({ label, value, highlight }) => (
  <div>
    <div className="text-xs text-text-tertiary uppercase tracking-wider">{label}</div>
    <div
      className={`text-sm mt-0.5 ${
        highlight ? 'text-primary-600 font-semibold text-lg' : 'text-text-primary font-medium'
      }`}
    >
      {highlight && value ? `₹${value}` : value ?? '-'}
    </div>
  </div>
);
